import { onBeforeUnmount, ref, type Ref } from "vue";

/**
 * Enregistrement audio réel du livre d'or vocal.
 *
 * Remplace le faux dictaphone de la maquette (compteur qui s'incrémente,
 * barres animées au hasard) par un vrai accès au micro : `MediaRecorder`
 * pour capturer le flux, `AnalyserNode` pour en tirer des niveaux qui
 * réagissent réellement à la voix.
 */

/** Motif d'échec, aligné sur le mapping d'erreurs de la caméra. */
export type RecorderError = "denied" | "not-found" | "insecure-context" | "unsupported" | "unknown";

export interface UseAudioRecorder {
  isRecording: Ref<boolean>;
  isPaused: Ref<boolean>;
  /** Temps écoulé depuis le début de l'enregistrement, en millisecondes. */
  elapsedMs: Ref<number>;
  /** 20 valeurs normalisées 0→1, alimentées par l'analyse fréquentielle du flux. */
  levels: Ref<number[]>;
  error: Ref<RecorderError | null>;
  result: Ref<{ blob: Blob; durationMs: number } | null>;
  // Syntaxe de propriété (et non de méthode) : ces fonctions n'utilisent
  // jamais `this`, et le consommateur les déstructure systématiquement
  // (`const { start, stop } = useAudioRecorder()`), ce que la syntaxe de
  // méthode interdit sans avertissement (@typescript-eslint/unbound-method).
  start: () => Promise<void>;
  stop: () => Promise<void>;
  cancel: () => void;
  reset: () => void;
}

/** Durée maximale d'un message, au-delà l'enregistrement s'arrête de lui-même. */
const MAX_DURATION_MS = 120_000;

/** Nombre de barres affichées par la waveform. */
const LEVEL_BAR_COUNT = 20;

/**
 * Types MIME candidats, du plus large au plus restreint. Le premier accepté
 * par `MediaRecorder.isTypeSupported` gouverne l'enregistrement.
 */
const CANDIDATE_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
];

const pickMimeType = (): string | null => {
  if (typeof MediaRecorder === "undefined") return null;
  return CANDIDATE_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type)) ?? null;
};

const mapGetUserMediaError = (error: unknown): RecorderError => {
  if (error instanceof DOMException) {
    if (error.name === "NotAllowedError" || error.name === "SecurityError") return "denied";
    if (error.name === "NotFoundError") return "not-found";
  }
  return "unknown";
};

const silentLevels = (): number[] => Array.from({ length: LEVEL_BAR_COUNT }, () => 0);

export function useAudioRecorder(): UseAudioRecorder {
  const isRecording = ref(false);
  const isPaused = ref(false);
  const elapsedMs = ref(0);
  const levels = ref<number[]>(silentLevels());
  const error = ref<RecorderError | null>(null);
  const result = ref<{ blob: Blob; durationMs: number } | null>(null);

  let stream: MediaStream | null = null;
  let mediaRecorder: MediaRecorder | null = null;
  let audioContext: AudioContext | null = null;
  let analyser: AnalyserNode | null = null;
  // Le paramètre de type est explicite : `Uint8Array` seul se résout en
  // `Uint8Array<ArrayBufferLike>`, que `getByteFrequencyData` refuse.
  let frequencyData: Uint8Array<ArrayBuffer> | null = null;
  let animationFrame: number | null = null;
  let startedAt = 0;
  let chunks: Blob[] = [];
  let mimeType = "";
  /** Génération du démarrage en cours, pour détecter un `cancel()` concurrent. */
  let startToken = 0;

  const stopAnimation = (): void => {
    if (animationFrame === null) return;
    cancelAnimationFrame(animationFrame);
    animationFrame = null;
  };

  const releaseStream = (): void => {
    stream?.getTracks().forEach((track) => track.stop());
    stream = null;
  };

  const closeAudioContext = (): void => {
    const context = audioContext;
    audioContext = null;
    analyser = null;
    frequencyData = null;
    if (context && context.state !== "closed") {
      context.close().catch(() => {
        // La fermeture peut échouer si le contexte est déjà en cours de
        // fermeture ; les ressources du flux ont de toute façon été libérées.
      });
    }
  };

  const releaseAll = (): void => {
    stopAnimation();
    releaseStream();
    closeAudioContext();
    mediaRecorder = null;
  };

  /** Boucle de mesure des niveaux, appelée à chaque frame pendant l'enregistrement. */
  const tick = (): void => {
    if (analyser && frequencyData) {
      analyser.getByteFrequencyData(frequencyData);
      const bucketSize = Math.max(1, Math.floor(frequencyData.length / LEVEL_BAR_COUNT));
      const next: number[] = [];
      for (let bar = 0; bar < LEVEL_BAR_COUNT; bar += 1) {
        let sum = 0;
        let count = 0;
        const start = bar * bucketSize;
        const end = Math.min(frequencyData.length, start + bucketSize);
        for (let index = start; index < end; index += 1) {
          sum += frequencyData[index] ?? 0;
          count += 1;
        }
        next.push(count > 0 ? sum / count / 255 : 0);
      }
      levels.value = next;
    }

    elapsedMs.value = performance.now() - startedAt;

    if (elapsedMs.value >= MAX_DURATION_MS) {
      stop().catch(() => {
        // stop() ne rejette pas dans les conditions normales ; en cas
        // d'échec du plafond automatique, l'utilisateur garde la main via
        // le bouton d'enregistrement.
      });
      return;
    }

    animationFrame = requestAnimationFrame(tick);
  };

  async function start(): Promise<void> {
    error.value = null;
    result.value = null;

    if (!navigator.mediaDevices?.getUserMedia) {
      error.value = "insecure-context";
      return;
    }

    const type = pickMimeType();
    if (!type) {
      error.value = "unsupported";
      return;
    }
    mimeType = type;

    const token = ++startToken;

    let media: MediaStream;
    try {
      media = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
    } catch (caught) {
      if (token !== startToken) return;
      error.value = mapGetUserMediaError(caught);
      return;
    }

    // L'invité a pu fermer la feuille pendant la demande d'autorisation : le
    // flux qui arrive ensuite est orphelin. Sans ce relâchement, l'indicateur
    // d'enregistrement du navigateur reste allumé indéfiniment.
    if (token !== startToken) {
      media.getTracks().forEach((track) => track.stop());
      return;
    }

    stream = media;

    try {
      audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      analyser = audioContext.createAnalyser();
      analyser.fftSize = 64;
      frequencyData = new Uint8Array(analyser.frequencyBinCount);
      source.connect(analyser);
    } catch {
      // La waveform est un agrément visuel : son échec ne doit pas empêcher
      // l'enregistrement de se dérouler.
      audioContext = null;
      analyser = null;
      frequencyData = null;
    }

    chunks = [];
    const recorder = new MediaRecorder(stream, { mimeType });
    recorder.addEventListener("dataavailable", (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    });
    mediaRecorder = recorder;
    recorder.start();

    isRecording.value = true;
    isPaused.value = false;
    startedAt = performance.now();
    elapsedMs.value = 0;
    levels.value = silentLevels();
    animationFrame = requestAnimationFrame(tick);
  }

  async function stop(): Promise<void> {
    const recorder = mediaRecorder;
    if (!recorder || !isRecording.value) return;

    // Le drapeau tombe AVANT l'appel natif, de façon synchrone : un second
    // appui sur le bouton d'arrêt est ainsi écarté par le garde ci-dessus, au
    // lieu de faire lever `InvalidStateError` à `recorder.stop()`.
    isRecording.value = false;
    isPaused.value = false;

    const token = startToken;
    const durationMs = performance.now() - startedAt;

    await new Promise<void>((resolve) => {
      recorder.addEventListener(
        "stop",
        () => {
          // L'évènement `stop` du MediaRecorder arrive de façon différée. Si un
          // `cancel()` est survenu entre-temps — l'invité ferme la feuille juste
          // après avoir arrêté — le jeton a changé et l'enregistrement ne doit
          // surtout pas être ressuscité : il le retrouverait à la réouverture
          // alors qu'il pensait l'avoir abandonné.
          if (token === startToken) {
            result.value = { blob: new Blob(chunks, { type: mimeType }), durationMs };
          }
          resolve();
        },
        { once: true },
      );

      try {
        recorder.stop();
      } catch (cause) {
        console.error("[voice] l'enregistreur était déjà arrêté", cause);
        resolve();
      }
    });

    if (token !== startToken) return;
    releaseAll();
  }

  function cancel(): void {
    // Invalide tout démarrage encore en vol : son flux sera relâché à l'arrivée.
    startToken += 1;
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
    }
    isRecording.value = false;
    isPaused.value = false;
    elapsedMs.value = 0;
    levels.value = silentLevels();
    releaseAll();
  }

  function reset(): void {
    result.value = null;
    error.value = null;
    elapsedMs.value = 0;
    levels.value = silentLevels();
  }

  onBeforeUnmount(() => {
    cancel();
  });

  return { isRecording, isPaused, elapsedMs, levels, error, result, start, stop, cancel, reset };
}
